<template>
    <div>
        <section>
            <div class="card mb-5">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm">
                            <div class="form-group">
                                <label>Start Date</label>
                                <datepicker v-model="params.start_date" format="MMM-d-yyyy" :clear-button="true" @input="setDate"></datepicker>
                            </div>
                        </div>
                        <div class="col-sm">
                            <div class="form-group">
                                <label>End Date</label>
                                <datepicker v-model="params.end_date" format="MMM-d-yyyy" :clear-button="true" @input="setDate"></datepicker>
                            </div>
                        </div>
                    </div>

                    <p>Or enter a list of batch numbers to include:</p>

                    <div class="row">
                        <div class="col-sm">
                            <div class="form-group">
                                <label>Batch Numbers</label>
                                <div class="input-group">
                                    <input type="text" class="form-control" v-model="params.batch_numbers" @input="setBatchNumbers">
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <div class="card-footer">
                    <div v-if="params.start_date && params.end_date && shortDate(params.start_date) == shortDate(params.end_date)" class="meta">
                        Reporting on {{ shortDate(params.start_date) }}.
                    </div>
                    <div v-else-if="params.start_date && params.end_date" class="meta">
                        Reporting on {{ shortDate(params.start_date) }} through {{ shortDate(params.end_date) }}.
                    </div>
                    <div v-else-if="params.start_date" class="meta">
                        Reporting on all orders on or after {{ shortDate(params.start_date) }}.
                    </div>
                    <div v-else-if="params.end_date" class="meta">
                        Reporting on all orders on or before {{ shortDate(params.end_date) }}.
                    </div>
                    <div v-else-if="batchNumbers.length > 1" class="meta">
                        Reporting on the following batch numbers: {{ batchNumbers.join(', ') }}.
                    </div>
                    <div v-else-if="batchNumbers.length == 1" class="meta">
                        Reporting on the batch number {{ batchNumbers[0] }}.
                    </div>
                    <div v-else>
                        Choose the dates above you’d like to report on
                    </div>
                </div>
            </div>
        </section>
        <section>

            <totals v-slot="{ totals }" :params="params">
                <div v-if="totals && totals.length">
                    <h2 class="h3">Grand Totals</h2>
                    <table class="table">
                        <thead>
                            <tr>
                                <th>&nbsp;</th>
                                <th scope="col">Orders</th>
                                <th scope="col">Gallons Delivered</th>
                                <th scope="col">Sales</th>
                                <th scope="col">Tax</th>
                                <th scope="col">Processing</th>
                                <th scope="col">Credit Card</th>
                                <th scope="col">Paid Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="total in totals.slice(0, 1)" :key="total.id">
                                <th scope="row" class="w-25">Totals</th>
                                <td v-text="total.orders"></td>
                                <td v-text="total.gallons"></td>
                                <td v-text="calculateSales(total)"></td>
                                <td v-text="calculateTax(total)"></td>
                                <td v-text="calculateProcessingFees(total)"></td>
                                <td v-text="calculateCreditCardFees(total)"></td>
                                <td v-text="calculatePaidOut(total)"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </totals>



            <totals-by-batch v-slot="{ totalsByBatch }" :params="params">
                <div v-if="totalsByBatch && totalsByBatch.length">
                    <hr class="my-5" />
                    <h2 class="h3">By Batch Number</h2>
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th scope="col">Batch Number</th>
                                <th scope="col">Orders</th>
                                <th scope="col">Gallons Delivered</th>
                                <th scope="col">Sales</th>
                                <th scope="col">Tax</th>
                                <th scope="col">Processing</th>
                                <th scope="col">Credit Card</th>
                                <th scope="col">Paid Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="row in totalsByBatch">
                                <tr>
                                    <th scope="row" class="w-25">
                                        <a v-if="row.batch_number != null" :href="linkForBatch(row.batch_number)" v-text="row.batch_number"></a>
                                        <a v-else :href="linkForBatch()">No Batch Number</a>
                                    </th>
                                    <td v-text="row.orders"></td>
                                    <td v-text="row.gallons"></td>
                                    <td v-text="calculateSales(row)"></td>
                                    <td v-text="calculateTax(row)"></td>
                                    <td v-text="calculateProcessingFees(row)"></td>
                                    <td v-text="calculateCreditCardFees(row)"></td>
                                    <td v-text="calculatePaidOut(row)"></td>
                                </tr>
                                <tr v-if="row.batch_notes">
                                    <td v-html="row.batch_notes" colspan="8"></td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </totals-by-batch>

            <template v-if="isAdmin">

                <totals-by-company v-slot="{ totalsByCompany }" :params="params">
                    <div v-if="totalsByCompany && totalsByCompany.length">
                        <hr class="my-5" />
                        <h2 class="h3">By Company</h2>

                        <table class="table table-striped" v-if="totalsByCompany">
                            <thead>
                                <tr>
                                    <th scope="col">Company</th>
                                    <th scope="col">Orders</th>
                                    <th scope="col">Gallons Delivered</th>
                                    <th scope="col">Sales</th>
                                    <th scope="col">Tax</th>
                                    <th scope="col">Processing</th>
                                    <th scope="col">Credit Card</th>
                                    <th scope="col">Paid Out</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row, company_id in totalsByCompany" :key="company_id">
                                    <th scope="row" class="w-25" v-text="row.name"></th>
                                    <td v-text="row.orders"></td>
                                    <td v-text="row.gallons"></td>
                                    <td v-text="calculateSales(row)"></td>
                                    <td v-text="calculateTax(row)"></td>
                                    <td v-text="calculateProcessingFees(row)"></td>
                                    <td v-text="calculateCreditCardFees(row)"></td>
                                    <td v-text="calculatePaidOut(row)"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </totals-by-company>
            </template>
        </section>
    </div>
</template>
<script>
import {ContinuousCollectionComponent, vueMixin} from 'clockvine-vue';
import Datepicker from 'vuejs-datepicker';

export default {
    props: ['isAdmin'],

    components: {
        Datepicker,
        totals: ContinuousCollectionComponent.for('totals'),
        totalsByBatch: ContinuousCollectionComponent.for('totalsByBatch'),
        totalsByCompany: ContinuousCollectionComponent.for('totalsByCompany'),
    },

    data: () => ({
        params: {
            start_date: '',
            end_date: '',
            batch_numbers: '',
        }
    }),

    computed: {
        batchNumbers() {
            return this.params.batch_numbers.split(/[^a-zA-Z0-9]+/).filter(n => n);
        },
    },

    methods: {
        calculateSales(record) {
            return this.usd(record.grand_total - record.tax_total)
        },
        calculateTax(record) {
            return this.usd(record.tax_total);
        },
        calculateProcessingFees(record) {
            return this.usd(record.processing_fee_total);
        },
        calculateCreditCardFees(record) {
            return this.usd(record.cc_fee_total);
        },
        calculatePaidOut(record) {
            return this.usd(record.grand_total - record.tax_total - record.processing_fee_total - record.cc_fee_total);
        },
        setDate() {
            this.params.batch_numbers = '';
        },

        setBatchNumbers() {
            this.params.start_date = '';
            this.params.end_date = '';
        },


        linkForBatch(batch) {
            let href = '/order/admin/orders?';

            if (batch) {
                href += 'keyword=' + batch + '&';
            } else {
                href += 'batched=no&';
            }

            return href + `from=${this.shortDate(this.params.start_date)}&through=${this.shortDate(this.params.end_date)}`;
        },
    },
}
</script>
